import React, { PropsWithChildren, memo } from 'react';
import { UniqueIdentifier, useDndContext } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import clsx from 'clsx';
import { isAllowedMoveQueue, parseColumnId } from '../utils';
import { selectOnlyTaskById, useKanbanEventStore } from '../store';
import { TaskStatus } from '@nexelem/graphne-api';
import { useDisableKanbanDnd } from './useDisableKanbanDnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan } from '@fortawesome/pro-light-svg-icons';

type DroppableContainerProps = {
  columnId: string;
  items: UniqueIdentifier[];
  className?: string;
};

export const DroppableContainer = memo(
  ({ children, columnId, items, className }: PropsWithChildren<DroppableContainerProps>) => {
    const disableKanbanDnd = useDisableKanbanDnd();
    const { setNodeRef } = useSortable({
      id: columnId,
      data: {
        type: 'container',
        children: items,
      },
    });
    const disabled = useIsContainerDisabled(columnId);

    // When disableKanbanDnd, disabled styles should not be visible
    const dndDisabeld = disableKanbanDnd || disabled;

    return (
      <div className={clsx(className, disabled && 'group disabled')} ref={dndDisabeld ? undefined : setNodeRef}>
        <SortableContext disabled={disabled} id={columnId} items={items} strategy={verticalListSortingStrategy}>
          {items.length === 0 && disabled && <DisabledIndicator />}
          {children}
        </SortableContext>
      </div>
    );
  },
);

DroppableContainer.displayName = 'DroppableContainer';

const DisabledIndicator = memo(() => (
  <div className="flex h-full w-full items-center justify-center">
    <FontAwesomeIcon className="min-h-6 min-w-6 text-icon-disabled" icon={faBan} />
  </div>
));

DisabledIndicator.displayName = 'DisabledIndicator';

const useIsContainerDisabled = (columnId: string) => {
  const { active } = useDndContext();
  const activeId = active?.id as number | undefined;

  const task = useKanbanEventStore(selectOnlyTaskById(activeId || 0));

  const fromStatus = active?.data.current?.taskStatus;
  const fromQueueId = active?.data.current?.queueId;
  const to = parseColumnId(columnId);

  if (!active || !task || !to || !to.queueId) {
    return false;
  }

  const possibleQueues = Object.keys(task?.estimatedDurations.durations || {});

  // move between same queue
  if (active && fromQueueId.toString() === to.queueId) {
    return !isAllowedMoveQueue({ fromStatus, toStatus: to.status });
  }

  // move between different queues, only possible for Pending status
  if (fromStatus === TaskStatus.Pending && to.status === TaskStatus.Pending) {
    return !possibleQueues.includes(to.queueId);
  }

  return true;
};
